import { ShieldCheck, ShieldX } from "lucide-react";
import React from "react";

import { useI18n } from "@/context/i18n";

export type TRedirectStatus = "success" | "error" | "loading";

interface IRedirectMessageProps {
  message: string;
  redirectTime: number;
  redirectFunction: () => void;
  icon?: JSX.Element;
  type: TRedirectStatus;
}

export interface IRedirectMessageData {
  success: {
    message: string;
    function: () => void;
    time: number;
  };
  error: {
    message: string;
    function: () => void;
    time: number;
  };
}

export interface IRedirectMessageRef {
  handleMessage: () => void;
}

export const RedirectMessage = React.forwardRef(
  (
    props: React.PropsWithChildren<IRedirectMessageProps>,
    ref: React.ForwardedRef<IRedirectMessageRef>
  ) => {
    const { message, redirectTime, redirectFunction, icon, type } = props;
    const [redirectCount, setRedirectCount] =
      React.useState<number>(redirectTime);
    const [open, setOpen] = React.useState<boolean>(false);
    const { t } = useI18n();

    const handleMessage = () => {
      setOpen((prevState) => {
        setRedirectCount(redirectTime);
        return !prevState;
      });
    };

    React.useImperativeHandle(ref, () => ({
      handleMessage,
    }));

    React.useEffect(() => {
      if (redirectCount <= redirectTime && redirectCount >= 1 && open) {
        setTimeout(() => {
          setRedirectCount((prev) => prev - 1);
        }, 1000);
      }

      if (redirectCount === 0 && open) {
        setOpen(false);
        redirectFunction();
      }
    }, [redirectCount, redirectTime, redirectFunction, open]);

    if (!open) return null;

    return (
      <div className="absolute inset-0 z-50 bg-background flex flex-1 w-full h-screen justify-center items-center">
        <div className="flex px-10 h-[60%] bg-background full lg:w-6/12 items-center justify-center border border-muted-20 shadow-lg">
          <div className="flex flex-col items-center justify-center min-h-96">
            {icon}
            {type === "success" && (
              <ShieldCheck className="w-32 h-32 text-green-700" />
            )}
            {type === "error" && <ShieldX className="w-32 h-32 text-red-800" />}
            <p className="text-3xl font-semibold text-center mt-8 text-foreground/80">
              {message}
            </p>
            <p className="text-xl text-center mt-8 text-foreground/80">
              {`${t(
                "components.redirectMessage.redirectIn"
              )} ${redirectCount} ${t(
                "components.redirectMessage.seconds"
              )} ...`}
            </p>
          </div>
        </div>
      </div>
    );
  }
);
